import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users2, Plus, Pencil, Trash2, Search, LayoutGrid, List, Camera, X } from "lucide-react";
import { uploadSmart } from "@/lib/upload";
import { trFold, SearchClear } from "@/components/shared";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const MemberAvatar = ({ m, size = 64 }) => {
  const [err, setErr] = useState(false);
  if (m.photo_file_id && !err) {
    return (
      <img
        src={`${API_BASE}/api/team-photos/${m.id}?v=${m.photo_file_id}`}
        onError={() => setErr(true)}
        alt={m.name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover shrink-0"
      />
    );
  }
  return (
    <div style={{ width: size, height: size, fontSize: Math.max(11, size * 0.3) }} className="rounded-full bg-emerald-900 text-white flex items-center justify-center font-heading font-bold shrink-0">
      {initials(m.name)}
    </div>
  );
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", role_title: "", department: "", email: "", phone: "", photo_file_id: null });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("grid");
  const [photoBusy, setPhotoBusy] = useState(false);

  const load = () => api.get("/team").then((r) => { setMembers(r.data); setLoaded(true); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", role_title: "", department: "", email: "", phone: "", photo_file_id: null }); setEditing({ id: null }); };
  const openEdit = (m) => { setForm({ name: m.name, role_title: m.role_title, department: m.department, email: m.email || "", phone: m.phone || "", photo_file_id: m.photo_file_id || null }); setEditing(m); };

  const pickPhoto = async (fileList) => {
    if (!fileList?.length) return;
    const f = fileList[0];
    if (!f.type.startsWith("image/")) return toast.error("Lütfen bir görsel dosyası seçin");
    setPhotoBusy(true);
    try {
      const uploaded = await uploadSmart([f]);
      setForm((p) => ({ ...p, photo_file_id: uploaded[0].id }));
      toast.success("Fotoğraf yüklendi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("İsim girin");
    setBusy(true);
    try {
      if (editing.id) await api.put(`/team/${editing.id}`, form);
      else await api.post("/team", form);
      toast.success(editing.id ? "Üye güncellendi" : "Üye eklendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Üye silindi");
      setMembers((xs) => xs.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const groups = useMemo(() => {
    const term = trFold(q.trim());
    const list = members.filter((m) => !term || trFold(m.name).includes(term) || trFold(m.role_title).includes(term) || trFold(m.department).includes(term));
    const map = new Map();
    list.forEach((m) => {
      const d = m.department || "Departmansız";
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(m);
    });
    return [...map.entries()];
  }, [members, q]);

  const departments = [...new Set(members.map((m) => m.department).filter(Boolean))];

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-slate-900 text-white p-2.5"><Users2 size={20} /></div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Takım</h1>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="team-count">{members.length} kişi · departmanlara göre ekip üyeleri</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="İsim veya rol ara..." data-testid="team-search-input" className="rounded-none border-slate-200 h-10 pl-9 pr-9 w-56" />
            <SearchClear value={q} onClear={() => setQ("")} testid="team-search-clear" />
          </div>
          <div className="flex border border-slate-200">
            <button onClick={() => setView("grid")} data-testid="view-grid-button" title="Kart görünümü" className={`p-2.5 transition-colors duration-200 ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-400 hover:text-slate-700"}`}><LayoutGrid size={15} /></button>
            <button onClick={() => setView("list")} data-testid="view-list-button" title="Liste görünümü" className={`p-2.5 transition-colors duration-200 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-400 hover:text-slate-700"}`}><List size={15} /></button>
          </div>
          <Button onClick={openNew} data-testid="add-member-button" className="rounded-none h-10 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
            <Plus size={15} /> Yeni Üye
          </Button>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-500" data-testid="team-empty">
          {loaded ? "Henüz ekip üyesi eklenmedi." : "Yükleniyor..."}
        </div>
      )}

      {groups.map(([dept, list]) => (
        <div key={dept} data-testid={`team-dept-${dept}`}>
          <div className="flex items-center gap-3">
            <div className="w-1 h-4 bg-slate-900" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold">{dept}</span>
            <span className="bg-slate-900 text-white text-[10px] font-bold w-4.5 h-4.5 px-1.5 py-0.5">{list.length}</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
          {view === "grid" ? (
          <div className="flex flex-wrap gap-x-12 gap-y-8 mt-6 pl-1">
            {list.map((m) => (
              <div key={m.id} className="flex flex-col items-center w-32 group" data-testid={`team-member-${m.id}`}>
                <MemberAvatar m={m} size={64} />
                <div className="text-sm font-semibold mt-3 text-center">{m.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5 text-center">{m.role_title || "-"}</div>
                <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`} title="Düzenle" className="text-slate-400 hover:text-slate-700 transition-colors duration-200"><Pencil size={13} /></button>
                  <button onClick={() => remove(m)} data-testid={`delete-member-${m.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
          ) : (
          <div className="border border-slate-200 bg-white mt-4 divide-y divide-slate-100">
            {list.map((m) => (
              <div key={m.id} className="px-4 py-3 flex items-center gap-4" data-testid={`team-member-${m.id}`}>
                <MemberAvatar m={m} size={38} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{m.role_title || "-"}</div>
                </div>
                <div className="hidden sm:block text-xs text-slate-500 truncate max-w-[220px]">{m.email || ""}</div>
                <div className="hidden md:block text-xs text-slate-500">{m.phone || ""}</div>
                <button onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`} title="Düzenle" className="text-slate-400 hover:text-slate-700 transition-colors duration-200"><Pencil size={13} /></button>
                <button onClick={() => remove(m)} data-testid={`delete-member-${m.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
          )}
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Users2 size={18} /> {editing?.id ? "Üyeyi Düzenle" : "Yeni Ekip Üyesi"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <MemberAvatar m={{ id: editing?.id, name: form.name, photo_file_id: editing?.id ? form.photo_file_id : null }} size={56} />
              <div className="flex items-center gap-2">
                <label className={`flex items-center gap-1.5 text-xs font-semibold border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors duration-200 ${photoBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="member-photo-label">
                  <Camera size={13} /> {photoBusy ? "Yükleniyor..." : form.photo_file_id ? "Fotoğrafı Değiştir" : "Fotoğraf Yükle"}
                  <input type="file" accept="image/*" className="hidden" data-testid="member-photo-input" onChange={(e) => { pickPhoto(e.target.files); e.target.value = ""; }} />
                </label>
                {form.photo_file_id && (
                  <button onClick={() => setForm((p) => ({ ...p, photo_file_id: null }))} data-testid="remove-photo-button" title="Fotoğrafı kaldır" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><X size={15} /></button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Ad Soyad</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="member-name-input" className="rounded-none border-slate-200 h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rol / Ünvan</Label>
                <Input value={form.role_title} onChange={(e) => setForm((f) => ({ ...f, role_title: e.target.value }))} data-testid="member-role-input" className="rounded-none border-slate-200 h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>Departman</Label>
                <Input list="team-departments" value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} data-testid="member-department-input" className="rounded-none border-slate-200 h-11" placeholder="örn. PMO" />
                <datalist id="team-departments">
                  {departments.map((d) => <option key={d} value={d} />)}
                </datalist>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>E-posta (ops.)</Label>
                <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="member-email-input" className="rounded-none border-slate-200 h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>Telefon (ops.)</Label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="member-phone-input" className="rounded-none border-slate-200 h-11" />
              </div>
            </div>
            <Button onClick={save} disabled={busy || !form.name.trim()} data-testid="save-member-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
              {busy ? "Kaydediliyor..." : editing?.id ? "Güncelle" : "Ekle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
